// =====================================================================
// Avatar
// =====================================================================
// Portraits dessinés en SVG inline : rien à télécharger, net à toutes les
// tailles, et le poids reste nul — ce qui compte sur un Tecno en 3G.
//
// Les visages sont noirs, parce que ce sont ceux des utilisateurs. Les
// teints varient légèrement d'une personne à l'autre, tirés de son
// identifiant, pour qu'une fratrie ne ressemble pas à une série de clones.
//
// Trois tranches d'âge, déduites de la génération affichée : les aînés
// ont les cheveux gris, les enfants un visage plus rond. Ce n'est pas une
// donnée saisie, c'est un indice visuel de lecture de l'arbre.
// =====================================================================

import type { Sexe } from '../../partage/types';

export type Tranche = 'enfant' | 'adulte' | 'ancien';

// Teints suffisamment contrastés avec le noir des cheveux : en dessous,
// le visage et la coiffure se confondent en une tache une fois réduits à
// 58 px dans l'arbre.
const TEINTS = ['#7a4e2f', '#8d5b38', '#6b4229', '#9a6540', '#714630'];
const HABITS = ['#2f4858', '#3c3f58', '#4a3b52', '#2d4a43', '#53391f'];

/** Tranche d'âge déduite de la position dans l'arbre. */
export function trancheDepuisGeneration(generation: number): Tranche {
  if (generation >= 2) return 'ancien';
  if (generation <= -1) return 'enfant';
  return 'adulte';
}

/** Choix stable : la même personne garde toujours le même teint. */
function graine(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

interface Props {
  id: string;
  sexe: Sexe | null;
  tranche?: Tranche;
  /** Personne sans identité connue : silhouette neutre. */
  inconnu?: boolean;
}

/**
 * Les formes, dans un repère 0 0 100 100. Utilisables aussi bien dans un
 * <svg> autonome que dans le grand SVG de l'arbre.
 */
export function FormesAvatar({ id, sexe, tranche = 'adulte', inconnu }: Props) {
  const g = graine(id);
  const teint = TEINTS[g % TEINTS.length];
  const habit = HABITS[(g >> 3) % HABITS.length];
  // Pas un noir absolu : il faut que la limite avec le visage se voie.
  // Les aînés ont les cheveux blancs — d'où un fond de médaillon un peu
  // plus soutenu, sans quoi la chevelure blanche s'y dissoudrait.
  const cheveux = tranche === 'ancien' ? '#e9e3d6' : '#241d18';
  const clip = `av-clip-${id}`;

  // Décalages stables, tirés de l'identifiant : la même personne cligne
  // toujours au même rythme, et deux voisins ne sont jamais synchrones.
  const retardYeux = ((g % 47) / 47) * 5.4;
  const retardSourire = ((g >> 5) % 53) / 53 * 7.2;

  const enfant = tranche === 'enfant';
  const ry = enfant ? 21 : 22;
  const rx = enfant ? 19 : 18.5;
  const cy = enfant ? 46 : 44;

  if (inconnu) {
    return (
      <>
        <circle cx="50" cy="50" r="50" className="av-fond-inconnu" />
        <circle cx="50" cy="42" r="17" className="av-silhouette" />
        <path
          d="M18 92a32 32 0 0 1 64 0z"
          className="av-silhouette"
        />
      </>
    );
  }

  return (
    <>
      <defs>
        <clipPath id={clip}>
          <circle cx="50" cy="50" r="50" />
        </clipPath>
      </defs>

      <circle cx="50" cy="50" r="50" className="av-fond" />

      <g clipPath={`url(#${clip})`}>
        {/* Buste */}
        <path
          d={`M50 ${cy + ry - 2}c-17 0-30 10-33 26l-4 30h74l-4-30c-3-16-16-26-33-26z`}
          fill={habit}
        />
        {/* Col */}
        <path
          d={`M50 ${cy + ry - 2}l-9 5 9 12 9-12z`}
          fill="#ffffff"
          opacity="0.9"
        />

        {/* Cou */}
        <path
          d={`M${50 - 7} ${cy + ry - 11}h14v12a7 7 0 0 1-14 0z`}
          fill={teint}
        />

        {/* Coiffure, masse arrière. Dessinée AVANT le visage : c'est elle
            qui donne la silhouette, et elle doit rester lisible à 58 px,
            donc une forme pleine, sans mèches fines qui deviendraient des
            taches une fois réduites. */}
        {sexe === 'F' && (
          <ellipse cx="50" cy={cy + 2} rx={rx + 5} ry={ry + 5} fill={cheveux} />
        )}

        {/* Oreilles — cachées par la coiffure féminine */}
        {sexe !== 'F' && (
          <>
            <ellipse cx={50 - rx - 1} cy={cy + 3} rx="3.4" ry="4.6" fill={teint} />
            <ellipse cx={50 + rx + 1} cy={cy + 3} rx="3.4" ry="4.6" fill={teint} />
          </>
        )}

        {/* Visage */}
        <ellipse cx="50" cy={cy} rx={rx} ry={ry} fill={teint} />

        {/* Coiffure, devant : une calotte nette posée sur le crâne.
            Chez l'aîné, elle se dégarnit légèrement. */}
        <path
          d={
            tranche === 'ancien' && sexe !== 'F'
              ? `M${50 - rx} ${cy - 1} A ${rx} ${ry} 0 0 1 ${50 + rx} ${cy - 1}` +
                ` L${50 + rx} ${cy - 6} Q 50 ${cy - 11} ${50 - rx} ${cy - 6} Z`
              : `M${50 - rx} ${cy + 1} A ${rx} ${ry} 0 0 1 ${50 + rx} ${cy + 1}` +
                ` L${50 + rx} ${cy - 5} Q 50 ${cy - 14} ${50 - rx} ${cy - 5} Z`
          }
          fill={cheveux}
        />

        {/* Regard. Le blanc de l'œil n'est pas décoratif : un œil uni sur
            une peau foncée, quand il se ferme, ne se voit pratiquement
            pas. Avec la sclère claire, le clignement se lit d'un coup.
            Le clignement et le sourire sont décalés d'une personne à
            l'autre : sans ce décalage, toute la famille cligne des yeux
            en même temps, ce qui est franchement inquiétant. */}
        {[-1, 1].map((cote) => (
          <g
            key={cote}
            className="av-oeil"
            style={{ animationDelay: `${retardYeux}s` }}
          >
            <ellipse
              cx={50 + cote * 6.9}
              cy={cy + 1.5}
              rx="3"
              ry="2.6"
              fill="#fdfbf7"
            />
            <circle cx={50 + cote * 6.9} cy={cy + 1.7} r="1.75" fill="#17110e" />
          </g>
        ))}

        {/* Sourire : bouche ouverte, avec l'éclat des dents. Un simple
            trait courbe se perd à 58 px ; une forme pleine tient. */}
        <g
          className="av-bouche"
          style={{ animationDelay: `${retardSourire}s` }}
        >
          <path
            d={`M${50 - 8} ${cy + 9} L${50 + 8} ${cy + 9} q-8 11 -16 0 z`}
            fill="#36211a"
          />
          <path
            d={`M${50 - 6.6} ${cy + 9.6} L${50 + 6.6} ${cy + 9.6} q-6.6 3.2 -13.2 0 z`}
            fill="#fdfbf7"
          />
        </g>
      </g>
    </>
  );
}

/** Avatar autonome, pour les contextes HTML (listes, fiche). */
export function Avatar({
  taille = 40,
  ...props
}: Props & { taille?: number }) {
  return (
    <svg
      className="avatar"
      width={taille}
      height={taille}
      viewBox="0 0 100 100"
      aria-hidden="true"
    >
      <FormesAvatar {...props} />
    </svg>
  );
}
